import { Flex, Icon, Spacing } from '@billy/design-system'
import { Badge, Button, Search } from '@shinetools/lumen-react'
import { ReactElement, useCallback } from 'react'

import { useBalanceConfig, useBalanceLabels } from '@features/balanceLedger'

import type { PeriodMode } from '../../../../enums/periodMode'
import { useDebouncedSearchInput } from '../../../../hooks/useDebouncedSearchInput'
import { useSuppliersBalanceFiltersContext } from '../../contexts/suppliersBalanceFiltersContext'
import { SuppliersBalanceFilterType } from '../../enums/suppliersBalanceFilterType'
import { useTrackFilterOpened } from '../../hooks/useTrackFilterOpened'
import { AddFilterMenu } from '../AddFilterMenu'
import { SuppliersBalanceExportMenu } from './elements/SuppliersBalanceExportMenu'
import * as Styled from './styles'

interface SuppliersBalanceToolbarProps {
  accountCount?: number
  periodType?: PeriodMode
}

export const SuppliersBalanceToolbar = ({ accountCount, periodType }: SuppliersBalanceToolbarProps): ReactElement => {
  const { filterTypeOrder } = useBalanceConfig()
  const { bt } = useBalanceLabels()
  const trackFilterOpened = useTrackFilterOpened({ periodType })
  const { search, setSearch, activeTypes, addedTypes, addFilter, isBarVisible, toggleBar } =
    useSuppliersBalanceFiltersContext()

  const filterCount = new Set([...activeTypes, ...addedTypes]).size
  const hasFilters = filterCount > 0

  const handleSearchUpdate = useCallback(
    (value: string) => {
      setSearch(value)
    },
    [setSearch],
  )

  const { onChange: handleSearchChange, value: searchValue } = useDebouncedSearchInput(search, handleSearchUpdate)

  const handleFilterButtonClick = useCallback(() => {
    // toggleBar also collapses an already-visible bar; a collapse is not an open.
    if (!isBarVisible) {
      trackFilterOpened()
    }
    toggleBar()
  }, [isBarVisible, toggleBar, trackFilterOpened])

  return (
    <Flex alignItems="center" gap={Spacing.S12} width="100%">
      <Flex.Item flexGrow={1}>
        <Styled.SearchWrapper>
          <Search
            rounded
            noDropdown
            onChange={handleSearchChange}
            placeholder={bt('toolbar.search.placeholder')}
            size="small"
            value={searchValue}
          />
        </Styled.SearchWrapper>
      </Flex.Item>
      {hasFilters ? (
        <Button
          endIcon={<Badge color="accent" count={filterCount} />}
          label={bt('filters.action.toggle')}
          onClick={handleFilterButtonClick}
          startIcon={<Icon color="currentColor" icon="funnel" size="sm" />}
          variant="outlined"
        />
      ) : (
        <AddFilterMenu
          availableTypes={filterTypeOrder as unknown as SuppliersBalanceFilterType[]}
          onOpen={trackFilterOpened}
          onSelect={addFilter}
          triggerElement={
            <Button
              label={bt('filters.action.toggle')}
              startIcon={<Icon color="currentColor" icon="funnel" size="sm" />}
              variant="outlined"
            />
          }
        />
      )}
      <SuppliersBalanceExportMenu accountCount={accountCount} periodType={periodType} />
    </Flex>
  )
}
